import { NativeStackScreenProps } from '@react-navigation/native-stack';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Image,
  Keyboard,
  KeyboardAvoidingView,
  Linking,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
  type TextInputProps,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useAuth } from '../auth/AuthContext';
import { useLocale } from '../i18n/LocaleContext';
import { LOCALE_LABELS } from '../i18n/messages';
import { useVisualTheme } from '../theme/VisualThemeContext';
import { CaptchaWidget } from '../components/CaptchaWidget';
import { LanguagePickerRow } from '../components/LanguagePicker';
import { track } from '../observability/analytics';
import { tryUnlockWebAudio } from '../util/playIncomingMessageSound';
import { humanizeError } from '../util/humanizeError';
import { readableErrorColor, relativeLuminance } from '../util/readableErrorColor';
import { PUBLIC_WEB_ORIGIN } from '../config';
import type { RootStackParamList } from '../navigation/types';
import type { PublicConfig } from '../api/types';
import {
  DISPLAY_NAME_MAX_CHARS,
  EMAIL_MAX_CHARS,
  PASSWORD_MAX_CHARS,
  SIGN_IN_IDENTIFIER_MAX_CHARS,
} from '../util/inputLimits';
import {
  fetchPublicConfig,
  forgotPassword,
  registerAccount,
  resendVerification,
  resetPassword,
  CaptchaRequiredError,
  HttpStatusError,
} from '../api/client';
import {
  authFailureMessage,
  canSelfRegister,
  passwordHintState,
  registrationClosedReason,
  registrationFieldProblem,
  registrationNextStep,
  registrationProblems,
  resetLinkProblem,
  resetLinkProblemMessage,
  resetTokenFromUrl,
  validateNewPassword,
  needsCaptcha,
  validateForgotEmail,
  validateLoginForm,
  verifyOutcomeFromUrl,
  type AuthFormMode,
  type RegistrationField,
  type ResetLinkProblem,
} from '../auth/authFlowRules';

type Props = NativeStackScreenProps<RootStackParamList, 'Login'>;

/**
 * Ссылка из письма ведёт на `/reset-password?token=…` — того же веб-приложения. Отдельного
 * маршрута у сборки нет, поэтому токен снимается прямо с адресной строки, и экран сразу
 * открывается формой нового пароля.
 *
 * <p>Возврат по ссылке подтверждения: API перенаправил сюда с итогом в параметре, потому
 * что показывать человеку пустую страницу — значит оборвать поток на последнем шаге.
 *
 * <p>Читается один раз, начальным значением состояния: адрес при открытии экрана не
 * меняется, а эффект показал бы сначала форму входа и только следующим кадром нужную.
 */
function authLinkFromUrl(): { resetToken: string | null; verifyOutcome: ReturnType<typeof verifyOutcomeFromUrl> } {
  if (Platform.OS !== 'web') return { resetToken: null, verifyOutcome: null };
  const href = globalThis.location?.href;
  const resetToken = resetTokenFromUrl(href);
  if (resetToken) return { resetToken, verifyOutcome: null };
  return { resetToken: null, verifyOutcome: verifyOutcomeFromUrl(href) };
}

type LoginChrome = ReturnType<typeof useVisualTheme>['loginChrome'];

/** Поле, у которого есть своя строка ошибки или подсказки. */
type FieldKey = 'login' | 'password' | RegistrationField;

/**
 * Поле формы с подписью для экранного диктора, необязательным «глазом» и строкой ошибки под
 * собой (аудит удобства 2026-10-04, п. 35–37).
 *
 * <p>Модульный компонент, а не функция внутри экрана: объявленный в рендере, он был бы для
 * React новым типом на каждой букве, и поле теряло бы фокус посреди набора.
 *
 * <p>Ошибка стоит под своим полем и объявляется диктором сразу (`accessibilityLiveRegion`,
 * роль `alert`): раньше она была одной строкой внизу формы, и по ней нельзя было понять,
 * какое из трёх полей поправить, а незрячий человек её вовсе не слышал.
 */
function AuthField({
  inputRef,
  chrome,
  errorColor,
  okColor,
  label,
  error,
  hint,
  hintOk,
  revealable,
  revealed,
  onToggleReveal,
  revealLabels,
  ...input
}: TextInputProps & {
  inputRef?: React.Ref<TextInput>;
  chrome: LoginChrome;
  errorColor: string;
  okColor: string;
  label: string;
  error?: string | null;
  hint?: string | null;
  hintOk?: boolean;
  revealable?: boolean;
  revealed?: boolean;
  onToggleReveal?: () => void;
  revealLabels?: { show: string; hide: string };
}): React.ReactElement {
  return (
    <View style={styles.fieldBlock}>
      <View
        style={[
          styles.inputRow,
          {
            backgroundColor: chrome.inputBg,
            borderColor: error ? errorColor : chrome.inputBorder,
          },
        ]}>
        <TextInput
          ref={inputRef}
          {...input}
          style={[styles.inputText, { color: chrome.inputText }]}
          placeholderTextColor={chrome.subColor}
          accessibilityLabel={label}
          aria-invalid={error ? true : undefined}
          secureTextEntry={revealable ? !revealed : input.secureTextEntry}
        />
        {revealable ? (
          <Pressable
            onPress={onToggleReveal}
            hitSlop={8}
            style={styles.eyeHit}
            accessibilityRole="button"
            accessibilityLabel={revealed ? revealLabels?.hide : revealLabels?.show}
            accessibilityState={{ checked: Boolean(revealed) }}>
            <Image
              source={require('../../assets/chat-icons/eye.png')}
              style={[styles.eyeIcon, { tintColor: chrome.subColor, opacity: revealed ? 1 : 0.45 }]}
              accessibilityIgnoresInvertColors
            />
          </Pressable>
        ) : null}
      </View>
      {error ? (
        <Text
          style={[styles.fieldError, { color: errorColor }]}
          accessibilityLiveRegion="assertive"
          accessibilityRole="alert">
          {error}
        </Text>
      ) : hint ? (
        <Text style={[styles.fieldHint, { color: hintOk ? okColor : chrome.subColor }]}>
          {hintOk ? `✓ ${hint}` : hint}
        </Text>
      ) : null}
    </View>
  );
}

/** Главная кнопка формы: с индикатором, иначе на медленной сети нажатие выглядит потерянным. */
function PrimaryButton({
  label,
  color,
  busy,
  onPress,
}: {
  label: string;
  color: string;
  busy: boolean;
  onPress: () => void;
}): React.ReactElement {
  return (
    <Pressable
      style={[styles.btn, { backgroundColor: color }]}
      onPress={onPress}
      disabled={busy}
      accessibilityRole="button"
      accessibilityLabel={label}
      accessibilityState={{ disabled: busy, busy }}>
      {busy ? <ActivityIndicator color="#fff" /> : <Text style={styles.btnText}>{label}</Text>}
    </Pressable>
  );
}

/** Ссылка-кнопка экрана входа: зона нажатия не меньше 44 pt (п. 16). */
function AuthLink({
  label,
  color,
  onPress,
  role = 'button',
}: {
  label: string;
  color: string;
  onPress: () => void;
  role?: 'button' | 'link';
}): React.ReactElement {
  return (
    <Pressable onPress={onPress} accessibilityRole={role} accessibilityLabel={label} style={styles.linkHit}>
      <Text style={[styles.link, { color }]}>{label}</Text>
    </Pressable>
  );
}

export function LoginScreen(_props: Props) {
  const insets = useSafeAreaInsets();
  const scrollRef = useRef<ScrollView>(null);
  const { login } = useAuth();
  const { t, locale, setLocale } = useLocale();
  const { visualTheme, loginChrome } = useVisualTheme();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Ошибки, привязанные к полям: каждая стоит под своим полем, а не общей строкой внизу. */
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<FieldKey, string>>>({});
  /** «Показать пароль»: одна настройка на экран — переход между формами её не сбрасывает. */
  const [showPassword, setShowPassword] = useState(false);
  const [langOpen, setLangOpen] = useState(false);

  const loginRef = useRef<TextInput>(null);
  const passwordRef = useRef<TextInput>(null);
  const emailRef = useRef<TextInput>(null);
  const regPasswordRef = useRef<TextInput>(null);
  const displayNameRef = useRef<TextInput>(null);

  /**
   * Регистрация и сброс пароля живут на этом же экране, а не отдельными маршрутами: все три
   * формы — это «вход, но иначе», и человек ходит между ними туда-сюда. Отдельные экраны
   * означали бы навигацию в стеке, из которого некуда возвращаться до входа.
   */
  const [authLink] = useState(authLinkFromUrl);
  const [mode, setMode] = useState<AuthFormMode>(() => (authLink.resetToken ? 'reset' : 'login'));
  const [config, setConfig] = useState<PublicConfig | null>(null);
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [notice, setNotice] = useState<string | null>(() => {
    if (authLink.verifyOutcome === 'ok') return t('verifyDone');
    if (authLink.verifyOutcome === 'failed') return t('verifyLinkDead');
    return null;
  });
  const resetToken = authLink.resetToken;
  /** Ссылка сброса не сработала — под ошибкой появляется «Прислать новую ссылку» (п. 39). */
  const [resetProblem, setResetProblem] = useState<ResetLinkProblem | null>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  /** Сервер ответил «нужна CAPTCHA» на вход. До этого виджета человек не видит (AUTH-4). */
  const [loginNeedsCaptcha, setLoginNeedsCaptcha] = useState(false);

  const errorColor = readableErrorColor(loginChrome.cardBg);
  const okColor = (relativeLuminance(loginChrome.cardBg) ?? 0) > 0.4 ? '#15803d' : '#86efac';

  /**
   * Довести форму до глаз, когда клавиатура уже поднялась.
   *
   * <p><b>Что было.</b> Карточка входа выше половины экрана: круглый логотип, название,
   * подзаголовок, эпиграф в три строки — и только под всем этим поля. С поднятой клавиатурой
   * на экран остаётся около 500 pt, карточке нужно около 580: поле пароля и кнопка входа
   * уезжали под клавиатуру ровно в тот момент, когда человек тапал по логину.
   *
   * <p><b>Почему `KeyboardAvoidingView` этого не делает.</b> Он только освобождает место —
   * добавляет снизу отступ в высоту клавиатуры. Прокрутить содержимое к полю, на которое
   * встал курсор, он не умеет, а `ScrollView` сам этого не делает ни на одной платформе:
   * список остаётся в начале, то есть на логотипе.
   *
   * <p><b>Почему `keyboardDidShow`, а не `onFocus` у поля.</b> На фокус клавиатуры ещё нет,
   * отступ снизу не добавлен, и прокручивать в этот момент некуда — `scrollToEnd` упёрся бы
   * в старую высоту содержимого. Событие «клавиатура показана» приходит уже после того, как
   * `KeyboardAvoidingView` отработал.
   *
   * <p>Именно `scrollToEnd`, а не прокрутка к конкретному полю: поля в этой карточке и так
   * стоят последними, а под ними только кнопка и ссылки — то есть конец списка и есть то
   * место, ради которого клавиатуру позвали.
   */
  useEffect(() => {
    const sub = Keyboard.addListener('keyboardDidShow', () => {
      scrollRef.current?.scrollToEnd({ animated: true });
    });
    return () => sub.remove();
  }, []);

  useEffect(() => {
    let cancelled = false;
    void fetchPublicConfig().then((cfg) => {
      if (!cancelled) setConfig(cfg);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  function clearErrors() {
    setError(null);
    setFieldErrors({});
  }

  function setFieldError(field: FieldKey, message: string | null) {
    setFieldErrors((prev) => {
      if ((prev[field] ?? null) === message) return prev;
      const next = { ...prev };
      if (message) next[field] = message;
      else delete next[field];
      return next;
    });
  }

  /**
   * Назад ко входу. Адрес, введённый при регистрации или восстановлении, переезжает в поле
   * логина (п. 6): человек только что его напечатал, и вводить его второй раз через минуту —
   * то самое «неудобно», на которое жаловались.
   */
  const backToLogin = useCallback(() => {
    setMode('login');
    setError(null);
    setFieldErrors({});
    setNotice(null);
    setResetProblem(null);
    setPassword('');
    const typed = email.trim();
    if (typed.length > 0) setUsername(typed);
  }, [email]);

  function openMode(next: AuthFormMode) {
    setMode(next);
    clearErrors();
    setResetProblem(null);
  }

  /**
   * Текст отказа сервера: сначала известные фразы своими словами (п. 39, «Invalid credentials»),
   * затем общий разбор — сеть, перегрузка, сырой английский (п. 19).
   */
  function failureText(e: unknown, fallback: string): string {
    if (e instanceof HttpStatusError) {
      const key = authFailureMessage(e.status, e.message);
      if (key) return t(key);
    }
    return humanizeError(e, fallback);
  }

  async function onRegister() {
    clearErrors();
    const problems = registrationProblems({ email, password, displayName });
    const first = (['email', 'password', 'displayName'] as const).find((f) => problems[f]);
    if (first) {
      setFieldErrors(
        Object.fromEntries(Object.entries(problems).map(([k, v]) => [k, t(v)])) as Partial<
          Record<FieldKey, string>
        >,
      );
      // Курсор — в первое поле с ошибкой: искать его глазами по форме не нужно.
      (first === 'email' ? emailRef : first === 'password' ? regPasswordRef : displayNameRef).current?.focus();
      return;
    }
    setBusy(true);
    let response: { verificationRequired: boolean };
    try {
      response = await registerAccount(email.trim(), password, displayName.trim(), captchaToken ?? undefined);
    } catch (err) {
      setCaptchaToken(null);
      setError(err instanceof CaptchaRequiredError ? t('captchaRequired') : failureText(err, t('registerFailed')));
      setBusy(false);
      return;
    }
    if (registrationNextStep(response) === 'verify') {
      setMode('verifySent');
      setBusy(false);
      return;
    }
    // Подтверждение почты на сервере выключено — аккаунт уже рабочий, входим сразу.
    try {
      await login(email.trim(), password);
    } catch (err) {
      // Аккаунт создан, а войти не вышло (сеть мигнула): ведём на вход с уже введённым адресом.
      backToLogin();
      setError(failureText(err, t('loginFailed')));
    } finally {
      setBusy(false);
    }
  }

  async function onResend() {
    setBusy(true);
    try {
      await resendVerification(email.trim());
    } catch {
      // Молчим намеренно: ответ на этот запрос ничего не сообщает о судьбе адреса, и
      // сетевая ошибка — единственное, о чём тут можно было бы рассказать.
    } finally {
      setNotice(t('verifyResendDone'));
      setBusy(false);
    }
  }

  async function onForgot() {
    clearErrors();
    // Без этой проверки форма отправляла пустой адрес: сервер требует @NotBlank @Email, и все
    // семнадцать попыток сброса в логах прода за 11–12 сентября кончились 400 (AUTH-13).
    const problem = validateForgotEmail(email);
    if (problem) {
      setFieldError('email', t(problem.message));
      emailRef.current?.focus();
      return;
    }
    setBusy(true);
    try {
      await forgotPassword(email.trim());
    } catch (err) {
      setError(failureText(err, t('errorGeneric')));
      setBusy(false);
      return;
    }
    setMode('forgotSent');
    setBusy(false);
  }

  async function onReset() {
    clearErrors();
    setResetProblem(null);
    const problem = validateNewPassword(password);
    if (problem) {
      setFieldError('password', t(problem.message));
      return;
    }
    setBusy(true);
    try {
      await resetPassword(resetToken ?? '', password);
      backToLogin();
      setNotice(t('resetDone'));
    } catch (err) {
      const linkProblem = err instanceof HttpStatusError ? resetLinkProblem(err.status, err.message) : null;
      if (linkProblem) {
        setResetProblem(linkProblem);
        setError(t(resetLinkProblemMessage(linkProblem)));
      } else {
        setError(failureText(err, t('resetFailed')));
      }
    } finally {
      setBusy(false);
    }
  }

  const serif = loginChrome.serifTitle
    ? Platform.select({ ios: 'Georgia', android: 'serif', default: undefined })
    : undefined;

  const epigraph =
    visualTheme === 'varangian' ? t('loginEpigraphVarangian') : t('loginEpigraphCalm');

  async function onSubmit() {
    if (busy) return;
    tryUnlockWebAudio();
    clearErrors();
    // AUTH-13. Форма обязана отвечать на пустое поле сама: до этой проверки пустой логин и
    // пустой пароль уезжали на сервер, а обратно приходил сырой текст Hibernate Validator —
    // на языке, который сервер выбрал по своему Accept-Language, а не по языку приложения.
    const problem = validateLoginForm({ login: username, password });
    if (problem) {
      const field: FieldKey = problem.field === 'password' ? 'password' : 'login';
      setFieldError(field, t(problem.message));
      (field === 'password' ? passwordRef : loginRef).current?.focus();
      return;
    }
    setBusy(true);
    try {
      await login(username.trim(), password, captchaToken ?? undefined);
    } catch (e) {
      // 428 — не «неверный пароль»: сервер просит подтвердить, что за формой человек.
      // Показать здесь обычную ошибку значило бы отправить его перебирать верный пароль.
      if (e instanceof CaptchaRequiredError) {
        setLoginNeedsCaptcha(true);
        setCaptchaToken(null);
        setError(t('captchaRequired'));
        setBusy(false);
        return;
      }
      // Событие уедет только после успешного входа: приёмник закрыт аутентификацией, и до неё
      // очередь ждёт. Для воронки «открыл — не смог войти — бросил» этого достаточно, а разбор
      // подбора паролей живёт на сервере (SEC-12), где видны и чужие клиенты.
      track('login_failed');
      setError(failureText(e, t('loginFailed')));
    } finally {
      setBusy(false);
    }
  }

  /** Проверка поля регистрации на уходе фокуса (п. 36): ошибка — сразу под ним. */
  function checkRegistrationField(field: RegistrationField) {
    const key = registrationFieldProblem(field, { email, password, displayName });
    setFieldError(field, key ? t(key) : null);
  }

  const fieldProps = { chrome: loginChrome, errorColor, okColor };
  const revealProps = {
    revealable: true,
    revealed: showPassword,
    onToggleReveal: () => setShowPassword((v) => !v),
    revealLabels: { show: t('loginShowPassword'), hide: t('loginHidePassword') },
  };
  const hint = passwordHintState(password);
  const generalError = error ? (
    <Text
      style={[styles.err, { color: errorColor }]}
      accessibilityLiveRegion="assertive"
      accessibilityRole="alert">
      {error}
    </Text>
  ) : null;

  const backLink = <AuthLink label={t('backToLogin')} color={loginChrome.subColor} onPress={backToLogin} />;

  return (
    <KeyboardAvoidingView
      style={[styles.root, { backgroundColor: loginChrome.rootBg }]}
      behavior={Platform.OS === 'ios' ? 'padding' : Platform.OS === 'android' ? 'height' : undefined}
      /**
       * Ноль, а не 88.
       *
       * <p>`keyboardVerticalOffset` — это расстояние от верха экрана до верха самого
       * `KeyboardAvoidingView`, и нужен он там, где сверху есть шапка навигации. У экрана
       * входа шапка выключена (`headerShown: false` в `GuestNavigator`), вид занимает экран
       * целиком — расстояние нулевое.
       *
       * <p>Прежние 88 не сдвигали содержимое вверх, а прибавлялись к отступу: внутри
       * компонента отступ считается как `frame.height - (keyboardScreenY - offset)`, то есть
       * «высота клавиатуры плюс offset». Под клавиатурой оставалась полоса пустого фона в
       * 88 pt, а карточка на те же 88 pt теряла место — её обрезало ровно по полю логина, что
       * и видно на скриншоте.
       */
      keyboardVerticalOffset={0}
      enabled={Platform.OS !== 'web'}>
      <ScrollView
        ref={scrollRef}
        keyboardShouldPersistTaps="handled"
        // Клавиатуру можно смахнуть вниз пальцем прямо по форме — с высокой карточкой это
        // единственный жест, которым до кнопки входа добираются, не целясь в пустое место.
        keyboardDismissMode={Platform.OS === 'ios' ? 'interactive' : 'on-drag'}
        contentContainerStyle={[
          styles.scrollInner,
          {
            paddingLeft: Math.max(24, insets.left + 16),
            paddingRight: Math.max(24, insets.right + 16),
            paddingTop: Math.max(24, insets.top + 16),
            paddingBottom: Math.max(24, insets.bottom + 24),
          },
        ]}
        showsVerticalScrollIndicator={false}>
        <View style={[styles.card, { backgroundColor: loginChrome.cardBg }]}>
          {/*
            Язык — прямо на карточке (аудит 2026-10-04, п. 32). Раньше выбрать его можно было
            только в настройках, то есть уже после входа: человек, которому достался чужой
            язык, должен был войти, не понимая подписей. Чип показывает язык его же словом
            («Русский», «English») — его узнают, даже не читая остального экрана.
          */}
          <View style={styles.langChipRow}>
            <Pressable
              onPress={() => setLangOpen((v) => !v)}
              style={[styles.langChip, { borderColor: loginChrome.inputBorder }]}
              hitSlop={6}
              accessibilityRole="button"
              accessibilityState={{ expanded: langOpen }}
              accessibilityLabel={t('loginLanguageChipA11y').replace(
                '{name}',
                LOCALE_LABELS[locale].a11yLabel,
              )}>
              <Text style={[styles.langChipText, { color: loginChrome.subColor }]}>
                {`${LOCALE_LABELS[locale].a11yLabel} ${langOpen ? '▴' : '▾'}`}
              </Text>
            </Pressable>
          </View>
          {langOpen ? (
            <LanguagePickerRow
              locale={locale}
              onSelect={(next) => {
                setLocale(next);
                setLangOpen(false);
              }}
              palette={{
                chipBg: loginChrome.inputBg,
                chipBorder: loginChrome.inputBorder,
                chipSelectedBg: loginChrome.btnBg,
                chipSelectedBorder: loginChrome.btnBg,
              }}
            />
          ) : null}
          <Image
            source={require('../../assets/icon.png')}
            style={styles.logo}
            accessibilityIgnoresInvertColors
            accessibilityRole="image"
            accessibilityLabel={t('appName')}
          />
          <Text
            style={[
              styles.title,
              { color: loginChrome.titleColor, fontFamily: serif },
              loginChrome.serifTitle
                ? Platform.select({
                    web: { textShadow: '0px 3px 14px rgba(0,0,0,0.9)' },
                    default: {
                      textShadowColor: 'rgba(0,0,0,0.9)',
                      textShadowOffset: { width: 0, height: 3 },
                      textShadowRadius: 14,
                    },
                  })
                : {},
            ]}
            accessibilityRole="header">
            {t('appName')}
          </Text>
          <Text style={[styles.sub, { color: loginChrome.subColor }]}>{t('appTagline')}</Text>

          <Text
            style={[
              styles.epigraph,
              {
                color: loginChrome.epigraphColor,
                fontFamily: serif,
                fontStyle: visualTheme === 'varangian' ? 'italic' : 'normal',
              },
              loginChrome.serifTitle
                ? Platform.select({
                    web: { textShadow: '0px 2px 8px rgba(0,0,0,0.75)' },
                    default: {
                      textShadowColor: 'rgba(0,0,0,0.75)',
                      textShadowOffset: { width: 0, height: 2 },
                      textShadowRadius: 8,
                    },
                  })
                : {},
            ]}>
            {epigraph}
          </Text>

          {mode === 'login' ? (
            <>
              {/*
                «Далее» на клавиатуре переводит в пароль, «Войти» на пароле — входит (п. 35).
                Раньше обе клавиши ничего не делали, и до кнопки входа приходилось тянуться
                мимо поднятой клавиатуры.
              */}
              <AuthField
                {...fieldProps}
                inputRef={loginRef}
                label={t('loginField')}
                placeholder={t('loginField')}
                value={username}
                onChangeText={(v) => {
                  setUsername(v);
                  setFieldError('login', null);
                }}
                maxLength={SIGN_IN_IDENTIFIER_MAX_CHARS}
                textContentType="username"
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect={false}
                returnKeyType="next"
                submitBehavior="submit"
                onSubmitEditing={() => passwordRef.current?.focus()}
                error={fieldErrors.login}
              />
              <AuthField
                {...fieldProps}
                {...revealProps}
                inputRef={passwordRef}
                label={t('loginPasswordA11y')}
                placeholder={t('passwordPlaceholder')}
                value={password}
                onChangeText={(v) => {
                  setPassword(v);
                  setFieldError('password', null);
                }}
                maxLength={PASSWORD_MAX_CHARS}
                textContentType="password"
                autoComplete="password"
                autoCapitalize="none"
                autoCorrect={false}
                returnKeyType="go"
                onSubmitEditing={() => void onSubmit()}
                error={fieldErrors.password}
              />
              {generalError}
              {notice ? <Text style={[styles.notice, { color: loginChrome.subColor }]}>{notice}</Text> : null}
            </>
          ) : mode === 'register' ? (
            <>
              <Text style={[styles.formTitle, { color: loginChrome.titleColor }]}>{t('registerTitle')}</Text>
              <AuthField
                {...fieldProps}
                inputRef={emailRef}
                label={t('registerEmailPlaceholder')}
                placeholder={t('registerEmailPlaceholder')}
                value={email}
                onChangeText={(v) => {
                  setEmail(v);
                  setFieldError('email', null);
                }}
                onBlur={() => checkRegistrationField('email')}
                maxLength={EMAIL_MAX_CHARS}
                autoCapitalize="none"
                autoCorrect={false}
                keyboardType="email-address"
                textContentType="emailAddress"
                autoComplete="email"
                returnKeyType="next"
                submitBehavior="submit"
                onSubmitEditing={() => regPasswordRef.current?.focus()}
                error={fieldErrors.email}
              />
              {/*
                Пароль при регистрации — «новый»: менеджер паролей предлагает сгенерировать
                надёжный и запоминает его под этим адресом, а не подставляет чужой старый.
              */}
              <AuthField
                {...fieldProps}
                {...revealProps}
                inputRef={regPasswordRef}
                label={t('loginPasswordA11y')}
                placeholder={t('passwordPlaceholder')}
                value={password}
                onChangeText={(v) => {
                  setPassword(v);
                  setFieldError('password', null);
                }}
                onBlur={() => checkRegistrationField('password')}
                maxLength={PASSWORD_MAX_CHARS}
                textContentType="newPassword"
                autoComplete="new-password"
                autoCapitalize="none"
                autoCorrect={false}
                returnKeyType="next"
                submitBehavior="submit"
                onSubmitEditing={() => displayNameRef.current?.focus()}
                error={fieldErrors.password}
                hint={t('passwordHintRule')}
                hintOk={hint === 'ok'}
              />
              <AuthField
                {...fieldProps}
                inputRef={displayNameRef}
                label={t('registerNamePlaceholder')}
                placeholder={t('registerNamePlaceholder')}
                value={displayName}
                onChangeText={(v) => {
                  setDisplayName(v);
                  setFieldError('displayName', null);
                }}
                maxLength={DISPLAY_NAME_MAX_CHARS}
                textContentType="name"
                autoComplete="name"
                returnKeyType="go"
                onSubmitEditing={() => void onRegister()}
                error={fieldErrors.displayName}
              />
              {generalError}
            </>
          ) : mode === 'verifySent' ? (
            <>
              <Text style={[styles.formTitle, { color: loginChrome.titleColor }]}>{t('verifySentTitle')}</Text>
              <Text style={[styles.formBody, { color: loginChrome.subColor }]}>
                {t('verifySentBody').replace('{email}', email.trim())}
              </Text>
              {notice ? <Text style={[styles.notice, { color: loginChrome.subColor }]}>{notice}</Text> : null}
            </>
          ) : mode === 'forgot' ? (
            <>
              <Text style={[styles.formTitle, { color: loginChrome.titleColor }]}>{t('forgotTitle')}</Text>
              <Text style={[styles.formBody, { color: loginChrome.subColor }]}>{t('forgotBody')}</Text>
              <AuthField
                {...fieldProps}
                inputRef={emailRef}
                label={t('registerEmailPlaceholder')}
                placeholder={t('registerEmailPlaceholder')}
                value={email}
                onChangeText={(v) => {
                  setEmail(v);
                  setFieldError('email', null);
                }}
                maxLength={EMAIL_MAX_CHARS}
                autoCapitalize="none"
                autoCorrect={false}
                keyboardType="email-address"
                textContentType="emailAddress"
                autoComplete="email"
                returnKeyType="send"
                onSubmitEditing={() => void onForgot()}
                error={fieldErrors.email}
              />
              {generalError}
            </>
          ) : mode === 'forgotSent' ? (
            <>
              <Text style={[styles.formTitle, { color: loginChrome.titleColor }]}>{t('forgotSentTitle')}</Text>
              <Text style={[styles.formBody, { color: loginChrome.subColor }]}>
                {t('forgotSentBody').replace('{email}', email.trim())}
              </Text>
            </>
          ) : (
            <>
              <Text style={[styles.formTitle, { color: loginChrome.titleColor }]}>{t('resetTitle')}</Text>
              <Text style={[styles.formBody, { color: loginChrome.subColor }]}>{t('resetBody')}</Text>
              <AuthField
                {...fieldProps}
                {...revealProps}
                label={t('resetNewPassword')}
                placeholder={t('resetNewPassword')}
                value={password}
                onChangeText={(v) => {
                  setPassword(v);
                  setFieldError('password', null);
                }}
                maxLength={PASSWORD_MAX_CHARS}
                textContentType="newPassword"
                autoComplete="new-password"
                autoCapitalize="none"
                autoCorrect={false}
                returnKeyType="go"
                onSubmitEditing={() => void onReset()}
                error={fieldErrors.password}
                hint={t('passwordHintRule')}
                hintOk={hint === 'ok'}
              />
              {generalError}
              {/*
                Протухшая или использованная ссылка (п. 39): раньше человек видел английское
                «Reset link has expired» и сам искал, где попросить новую. Теперь кнопка ведёт
                прямо в «Забыли пароль?» — адрес, если он уже введён, остаётся в поле.
              */}
              {resetProblem ? (
                <AuthLink
                  label={t('resetRequestNewLink')}
                  color={loginChrome.titleColor}
                  onPress={() => openMode('forgot')}
                />
              ) : null}
            </>
          )}

          {needsCaptcha(mode, config, loginNeedsCaptcha) ? (
            <CaptchaWidget
              siteKey={config?.captchaSiteKey ?? ''}
              onToken={setCaptchaToken}
            />
          ) : null}

          {mode === 'login' ? (
            <>
              <PrimaryButton
                label={t('signIn')}
                color={loginChrome.btnBg}
                busy={busy}
                onPress={() => void onSubmit()}
              />
              {/* Ссылки показываем по ответу сервера: режим регистрации живёт там, и зашитый
                  в клиент ответ разошёлся бы с ним при первом переключении (AUTH-2). */}
              {canSelfRegister(config) ? (
                <AuthLink
                  label={t('registerAction')}
                  color={loginChrome.subColor}
                  onPress={() => openMode('register')}
                />
              ) : registrationClosedReason(config) ? (
                <Text style={[styles.notice, { color: loginChrome.subColor }]}>
                  {t(registrationClosedReason(config)!)}
                </Text>
              ) : null}
              <AuthLink
                label={t('forgotAction')}
                color={loginChrome.subColor}
                onPress={() => {
                  // Адрес, уже набранный в поле логина, переносим: «забыл пароль» обычно
                  // вспоминают ровно после неудачной попытки с этим адресом.
                  if (username.includes('@') && email.trim().length === 0) setEmail(username.trim());
                  openMode('forgot');
                }}
              />
            </>
          ) : mode === 'register' ? (
            <>
              <PrimaryButton
                label={t('registerSubmit')}
                color={loginChrome.btnBg}
                busy={busy}
                onPress={() => void onRegister()}
              />
              {/* Документы, с которыми человек соглашается, — до нажатия, а не после (п. 5). */}
              <Text style={[styles.notice, { color: loginChrome.subColor }]}>{t('registerLegalLead')}</Text>
              <View style={styles.legalRow}>
                <AuthLink
                  role="link"
                  label={t('settingsTermsLink')}
                  color={loginChrome.subColor}
                  onPress={() => void Linking.openURL(`${PUBLIC_WEB_ORIGIN}/legal/terms`)}
                />
                <AuthLink
                  role="link"
                  label={t('settingsPrivacyPolicyLink')}
                  color={loginChrome.subColor}
                  onPress={() => void Linking.openURL(`${PUBLIC_WEB_ORIGIN}/legal/privacy`)}
                />
              </View>
              {backLink}
            </>
          ) : mode === 'verifySent' ? (
            <>
              <PrimaryButton
                label={t('verifyResend')}
                color={loginChrome.btnBg}
                busy={busy}
                onPress={() => void onResend()}
              />
              {backLink}
            </>
          ) : mode === 'forgot' ? (
            <>
              <PrimaryButton
                label={t('forgotSubmit')}
                color={loginChrome.btnBg}
                busy={busy}
                onPress={() => void onForgot()}
              />
              {backLink}
            </>
          ) : mode === 'forgotSent' ? (
            backLink
          ) : (
            <>
              <PrimaryButton
                label={t('resetSubmit')}
                color={loginChrome.btnBg}
                busy={busy}
                onPress={() => void onReset()}
              />
              {backLink}
            </>
          )}
        </View>
      </ScrollView>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  root: { flex: 1 },
  scrollInner: {
    flexGrow: 1,
    justifyContent: 'center',
    paddingVertical: 16,
  },
  card: {
    // На широком окне (десктоп, веб) карточка иначе растягивалась на весь экран — поля по
    // 1900 px. На телефоне ширина экрана меньше предела, и там ничего не меняется.
    width: '100%',
    maxWidth: 480,
    alignSelf: 'center',
    borderRadius: 16,
    padding: 20,
    gap: 12,
  },
  langChipRow: { flexDirection: 'row', justifyContent: 'flex-end' },
  langChip: {
    minHeight: 36,
    paddingHorizontal: 12,
    borderRadius: 18,
    borderWidth: 1,
    justifyContent: 'center',
  },
  langChipText: { fontSize: 13, fontWeight: '600' },
  logo: {
    width: 88,
    height: 88,
    alignSelf: 'center',
    borderRadius: 44,
    marginBottom: 4,
  },
  title: {
    fontSize: 26,
    fontWeight: '800',
    letterSpacing: 0.4,
    textAlign: 'center',
    lineHeight: 32,
  },
  sub: { marginBottom: 4, textAlign: 'center' },
  epigraph: {
    fontSize: 15,
    lineHeight: 24,
    textAlign: 'center',
    fontWeight: '500',
    letterSpacing: 0.15,
    marginBottom: 8,
    paddingHorizontal: 4,
  },
  fieldBlock: { gap: 4 },
  inputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 12,
    borderWidth: 1,
  },
  inputText: {
    flex: 1,
    paddingHorizontal: 14,
    paddingVertical: 12,
    minHeight: 46,
  },
  eyeHit: {
    width: 44,
    height: 44,
    alignItems: 'center',
    justifyContent: 'center',
  },
  eyeIcon: { width: 22, height: 22, resizeMode: 'contain' },
  fieldError: { fontSize: 13, lineHeight: 18, paddingHorizontal: 4, fontWeight: '500' },
  fieldHint: { fontSize: 12, lineHeight: 17, paddingHorizontal: 4 },
  err: { fontSize: 14, lineHeight: 20, fontWeight: '500' },
  notice: { fontSize: 13, textAlign: 'center', lineHeight: 18 },
  formTitle: { fontSize: 18, fontWeight: '700', textAlign: 'center' },
  formBody: { fontSize: 13, lineHeight: 19, textAlign: 'center' },
  legalRow: { flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', columnGap: 16 },
  linkHit: { minHeight: 44, justifyContent: 'center', alignItems: 'center' },
  link: { fontSize: 13, textAlign: 'center', textDecorationLine: 'underline' },
  btn: {
    borderRadius: 12,
    paddingVertical: 14,
    alignItems: 'center',
    marginTop: 4,
  },
  btnText: { color: '#fff', fontWeight: '600', fontSize: 16 },
});
